import type { ClientMessage, ServerMessage } from '@listen-draw/shared';
import { useStore } from './store.js';
import { track } from './analytics.js';
import { errorMessage } from './i18n.js';
import { wsUrl } from './api.js';

type Hello = Extract<ClientMessage, { type: 'hello' }>;

/**
 * 再接続付き WebSocket クライアント。
 * - hello をつないだ直後に送る（再接続時はトークンで復帰）
 * - 5 秒ごとに ping してサーバー時刻とのオフセットを推定（RTT が最小のサンプルを採用）
 */
export class GameSocket {
  private ws: WebSocket | null = null;
  private hello: Hello;
  private closedByUser = false;
  private attempt = 0;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private bestRtt = Infinity;
  private bestRttAt = 0;

  constructor(hello: Hello) {
    this.hello = hello;
  }

  connect(): void {
    this.closedByUser = false;
    const store = useStore.getState();
    store.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(wsUrl(this.hello.roomCode));
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      store.setStatus('open');
      const s = useStore.getState();
      const hello: Hello = { ...this.hello };
      if (hello.role === 'player' && s.token) hello.playerToken = s.token;
      ws.send(JSON.stringify(hello));
      this.startPing();
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      this.handle(msg);
    };
    ws.onclose = () => {
      this.stopPing();
      if (this.closedByUser || useStore.getState().fatal) {
        useStore.getState().setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose が続く */
    };
  }

  close(): void {
    this.closedByUser = true;
    this.stopPing();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.ws?.close(1000, 'bye');
    this.ws = null;
  }

  send(msg: ClientMessage): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  private handle(msg: ServerMessage): void {
    const store = useStore.getState();
    switch (msg.type) {
      case 'welcome':
        store.setWelcome(msg.role, msg.token, msg.playerId, msg.state, msg.myAnswer ?? null);
        if (msg.role === 'player' && this.hello.role === 'player')
          this.hello.playerToken = msg.token;
        break;
      case 'state':
        store.setState(msg.state);
        break;
      case 'pong': {
        const now = Date.now();
        const rtt = now - msg.t;
        // 最良 RTT のサンプルを 60 秒ごとに更新
        if (rtt <= this.bestRtt || now - this.bestRttAt > 60_000) {
          this.bestRtt = rtt;
          this.bestRttAt = now;
          store.setClock(msg.serverTime - (msg.t + rtt / 2), rtt);
        }
        break;
      }
      case 'event':
        store.pushEvent(msg.event);
        break;
      case 'answer:ack':
        store.setMyAnswer(msg.answer);
        break;
      case 'audio:play':
        store.setAudio({
          id: Date.now(),
          items: msg.items,
          rate: msg.rate,
          gapMs: msg.gapMs,
          replay: msg.replay,
        });
        break;
      case 'audio:stop':
        store.setAudio(null);
        break;
      case 'error':
        if (msg.fatal) track('game_error', { mode: 'multiplayer' });
        if (msg.fatal) {
          store.setFatal({ code: msg.code, message: msg.message });
        } else {
          store.toast(errorMessage(msg.code), 'bad');
        }
        break;
    }
  }

  private startPing(): void {
    this.stopPing();
    const ping = () => this.send({ type: 'ping', t: Date.now() });
    ping();
    window.setTimeout(ping, 800);
    this.pingTimer = window.setInterval(ping, 5000);
  }
  private stopPing(): void {
    if (this.pingTimer) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
  private scheduleReconnect(): void {
    this.attempt++;
    const delay = Math.min(8000, 400 * 2 ** Math.min(this.attempt, 5)) + Math.random() * 300;
    useStore.getState().setStatus('reconnecting');
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }
}
